import { useState } from "react"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { ArrowRight, Loader2, CheckCircle } from "lucide-react"

const STORAGE_KEY = "vorro_resource_access"

// Helper function to check if user already has access - call this before opening modal
export function checkResourceAccess(): boolean {
  try {
    const storedAccess = localStorage.getItem(STORAGE_KEY)
    if (storedAccess) {
      const accessData = JSON.parse(storedAccess)
      const thirtyDaysMs = 30 * 24 * 60 * 60 * 1000
      if (accessData.timestamp && Date.now() - accessData.timestamp < thirtyDaysMs) {
        return true
      }
    }
  } catch {
    localStorage.removeItem(STORAGE_KEY)
  }
  return false
}

interface ResourceGateModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  resourceUrl: string
  resourceName?: string
}

export function ResourceGateModal({ open, onOpenChange, resourceUrl, resourceName = "n8n Node Documentation" }: ResourceGateModalProps) {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    company: "",
    businessEmail: "",
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [isSuccess, setIsSuccess] = useState(false)

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value })
    setSubmitError(null)
  }

  const isValidBusinessEmail = (email: string) => {
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(email)) return false
    
    // Block common personal email domains
    const personalDomains = ['gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'aol.com', 'icloud.com', 'mail.com', 'protonmail.com']
    const domain = email.split('@')[1]?.toLowerCase()
    return !personalDomains.includes(domain)
  }

  const handleSubmit = async () => {
    if (!formData.firstName || !formData.lastName || !formData.company || !formData.businessEmail) {
      setSubmitError("Please fill in all fields")
      return
    }

    if (!isValidBusinessEmail(formData.businessEmail)) {
      setSubmitError("Please use a business email address")
      return
    }
    
    setIsSubmitting(true)
    setSubmitError(null)
    
    const landingPageUrl = window.location.href
    
    try {
      // Send to Zoho Flow webhook using no-cors mode with form-encoded data
      // Hardcoded URL since environment variables don't work in static WordPress builds
      const zohoWebhookUrl = 'https://flow.zoho.in/60025246573/flow/webhook/incoming?zapikey=1001.798d0a12be476204c80f68bc881af0cc.448d2c6acd629c92f719039ffb65a43b&isdebug=false'
      
      // Create form data for Zoho (use URLSearchParams for form-encoded data)
      // Keys must match Zoho CRM field names exactly (Pascal_Case)
      const formDataPayload = new URLSearchParams()
      formDataPayload.append('First_Name', formData.firstName)
      formDataPayload.append('Last_Name', formData.lastName)
      formDataPayload.append('Email', formData.businessEmail)
      formDataPayload.append('Company', formData.company)
      formDataPayload.append('Lead_Source', 'BridgeGate N8N')
      formDataPayload.append('Landing_Page_URL', landingPageUrl)
      
      // Use no-cors mode to avoid CORS issues with Zoho
      // Note: We can't read the response in no-cors mode, but the request will be sent
      await fetch(zohoWebhookUrl, {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataPayload.toString(),
      })

      // Optionally send to WordPress API (non-blocking, fire and forget)
      fetch('https://vorro.net/wp-json/vorro-leads/v1/submit', {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          name: `${formData.firstName} ${formData.lastName}`,
          email: formData.businessEmail,
          company: formData.company,
          selected_date: '',
          selected_time: '',
          source: `resource-download:${resourceName}`,
          landing_page: 'BridgeGate N8N node',
          landing_page_url: landingPageUrl,
        }).toString(),
      }).catch(() => {
        // Silently ignore WordPress API errors
      })
      
      // Store access in localStorage so user doesn't need to fill form again
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        email: formData.businessEmail,
        timestamp: Date.now(),
      }))
      
      // Show success state
      setIsSuccess(true)
      
      // Open resource in new tab and close modal after 1.5 seconds
      setTimeout(() => {
        window.open(resourceUrl, '_blank')
        onOpenChange(false)
        // Reset form after closing
        setTimeout(() => {
          setFormData({ firstName: "", lastName: "", company: "", businessEmail: "" })
          setIsSuccess(false)
        }, 300)
      }, 1500)
      
    } catch (error) {
      console.error('Submit error:', error)
      // Provide a more helpful error message for network/CORS issues
      if (error instanceof TypeError && error.message === 'Failed to fetch') {
        setSubmitError('Unable to connect to the server. Please ensure the API is configured and try again.')
      } else {
        setSubmitError(error instanceof Error ? error.message : 'Something went wrong. Please try again.')
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = (open: boolean) => {
    if (!open) {
      // Reset form on close
      setTimeout(() => {
        setFormData({ firstName: "", lastName: "", company: "", businessEmail: "" })
        setSubmitError(null)
        setIsSuccess(false)
      }, 300)
    }
    onOpenChange(open)
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[480px] p-0 overflow-hidden !bg-white rounded-2xl border-0 shadow-xl">
        {isSuccess ? (
          <div className="p-8 text-center">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <h3 className="text-xl font-bold text-[#2d2d3a] mb-2">Access Granted!</h3>
            <p className="text-[#6b6b7b]">
              Opening the {resourceName} in a new tab...
            </p>
          </div>
        ) : (
          <div className="p-6 sm:p-8">
            <DialogHeader className="mb-6">
              <DialogTitle className="text-2xl font-bold text-[#2d2d3a]">
                Access Free n8n Node
              </DialogTitle>
              <DialogDescription className="text-[#6b6b7b] mt-2">
                Enter your details to get instant access to the BridgeGate n8n node documentation and resources.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-[#6b6b7b] uppercase tracking-wider mb-2">
                    First Name
                  </label>
                  <Input
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    placeholder="John"
                    className="h-12 rounded-xl border-gray-200 focus:border-[#9e3667] focus:ring-[#9e3667]/20"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#6b6b7b] uppercase tracking-wider mb-2">
                    Last Name
                  </label>
                  <Input
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    placeholder="Doe"
                    className="h-12 rounded-xl border-gray-200 focus:border-[#9e3667] focus:ring-[#9e3667]/20"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-[#6b6b7b] uppercase tracking-wider mb-2">
                  Company
                </label>
                <Input
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                  placeholder="Acme Inc."
                  className="h-12 rounded-xl border-gray-200 focus:border-[#9e3667] focus:ring-[#9e3667]/20"
                />
              </div>
              
              <div>
                <label className="block text-xs font-medium text-[#6b6b7b] uppercase tracking-wider mb-2">
                  Business Email
                </label>
                <Input
                  name="businessEmail"
                  type="email"
                  value={formData.businessEmail}
                  onChange={handleInputChange}
                  placeholder="john@company.com"
                  className="h-12 rounded-xl border-gray-200 focus:border-[#9e3667] focus:ring-[#9e3667]/20"
                />
                <p className="text-xs text-[#9b9ba8] mt-1">Please use your work email address</p>
              </div>

              {submitError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                  {submitError}
                </div>
              )}

              <Button
                onClick={handleSubmit}
                disabled={isSubmitting || !formData.firstName || !formData.lastName || !formData.company || !formData.businessEmail}
                className="w-full h-14 rounded-xl text-white font-semibold text-lg bg-[linear-gradient(135deg,#a63083,#bf406a)] hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin mr-2" />
                    Processing...
                  </>
                ) : (
                  <>
                    Get Free Access
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </>
                )}
              </Button>

              <p className="text-xs text-center text-[#9b9ba8]">
                By submitting, you agree to receive product updates. No spam, ever.
              </p>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
